import React from "react";
import { dateStrToObj, dateObjToShortStr } from "../../requests/utilities/helpers";
import { entityLabel, pivotEntityPeriods, pivotEntityTotals } from "./entityPivot";
import { formatQhClock } from "./chartFormat";

function formatNumber(value, format) {
  if (value === null || value === undefined || isNaN(value)) return "—";

  switch (format) {
    case "dollars":
      return `$${parseFloat(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    case "percentage":
      return `${(value * 100).toFixed(2)}%`;
    case "number":
      return `${parseFloat(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    default:
      return value.toFixed(2);
  }
}

function getMetricLabel(metricValue, metrics) {
  if (!metricValue || !metrics || !Array.isArray(metrics)) {
    return metricValue || "Unknown";
  }

  let baseValue = metricValue;
  let suffix = "";

  if (metricValue.endsWith("_previous_year")) {
    baseValue = metricValue.replace("_previous_year", "");
    suffix = " (Prev Year)";
  }

  const metric = metrics.find((m) => m && m.value === baseValue);
  if (metric && metric.label) {
    return `${metric.label}${suffix}`;
  }
  return metricValue;
}

const TableWidget = ({
  activeTab,
  intradayData,
  data,
  rawData,
  selectedMetrics,
  metrics,
  compareLocations,
  compareDow,
  isDashboard,
  locationData,
  layout,
}) => {
  const safeMetrics = metrics || [];
  const safeSelectedMetrics = selectedMetrics || [];
  const safeData = data || [];
  const safeIntradayData = intradayData || [];
  const safeRawData = rawData || [];

  const firstMetric = safeMetrics.find((m) => m && m.value === safeSelectedMetrics[0]);
  const formatType = firstMetric && firstMetric.format ? firstMetric.format : "number";

  // by_entity: per store/employee/role, a bold total row followed by its
  // rows per period, one column per metric in that metric's own format.
  const renderEntityTable = (totals) => {
    const intraday = activeTab === "Intraday";
    const periods = intraday
      ? pivotEntityPeriods(safeIntradayData, safeSelectedMetrics, { intraday: true })
      : pivotEntityPeriods(safeRawData, safeSelectedMetrics);
    const multiDay = intraday && new Set(safeIntradayData.map((row) => row && row.date)).size > 1;
    const metricFormat = (metric) => {
      const info = safeMetrics.find((m) => m && m.value === metric);
      return (info && info.format) || "number";
    };
    const periodLabel = (period) => {
      const { date } = period;
      const formattedDate = typeof date === "string" ? dateObjToShortStr(dateStrToObj(date)) : date;
      if (intraday) {
        const qh = Number(period.qh_num);
        const time = formatQhClock(qh);
        return multiDay ? `${formattedDate} ${time}` : time;
      }
      return activeTab === "Weekly" ? `Week of ${formattedDate}` : formattedDate;
    };
    const cellClass = "px-3 py-2 text-sm text-gray-900";

    return (
      <div className={`overflow-auto ${isDashboard ? 'max-h-[600px]' : 'max-h-full'}`}>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{entityLabel(locationData)}</th>
              {periods && (
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
              )}
              {safeSelectedMetrics.map((metric) => (
                <th key={metric} className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  {getMetricLabel(metric, safeMetrics)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {totals.map((total) => (
              <React.Fragment key={total.entity}>
                <tr className={periods ? "bg-gray-50 font-semibold" : "hover:bg-gray-50"}>
                  <td className={cellClass}>{total.entity}</td>
                  {periods && <td className={cellClass}>Total</td>}
                  {safeSelectedMetrics.map((metric) => (
                    <td key={metric} className={`${cellClass} text-right`}>
                      {formatNumber(total[metric], metricFormat(metric))}
                    </td>
                  ))}
                </tr>
                {periods && (periods[total.entity] || []).map((period) => (
                  <tr key={`${total.entity}-${period.date}-${period.qh_num ?? ""}`} className="hover:bg-gray-50">
                    <td className={cellClass} />
                    <td className={`${cellClass} whitespace-nowrap`}>{periodLabel(period)}</td>
                    {safeSelectedMetrics.map((metric) => (
                      <td key={metric} className={`${cellClass} text-right`}>
                        {formatNumber(period[metric], metricFormat(metric))}
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (layout === "by_entity") {
    const entityTotals = pivotEntityTotals(locationData, safeSelectedMetrics);
    if (entityTotals.length > 0) {
      return <div className="h-full bg-white">{renderEntityTable(entityTotals)}</div>;
    }
  }

  const renderTable = () => {
    if (activeTab === "Intraday" && safeIntradayData?.length > 0) {
      if (compareDow) {
        // Group by time and show days of week
        const groupedByQh = safeIntradayData.reduce((acc, row) => {
          if (!row || !row.qh_num || !row.day_of_week) return acc;
          const qh = row.qh_num;
          if (!acc[qh]) acc[qh] = { time: qh };
          if (safeSelectedMetrics[0]) {
            acc[qh][row.day_of_week] = row[safeSelectedMetrics[0]];
          }
          return acc;
        }, {});

        const tableData = Object.values(groupedByQh)
          .sort((a, b) => a.time - b.time);

        const dayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
        const dayColumns = [...new Set(safeIntradayData.map(d => d.day_of_week).filter(Boolean))]
          .sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));

        return (
          <div className={`overflow-auto ${isDashboard ? 'max-h-[600px]' : 'max-h-full'}`}>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  {dayColumns.map(day => (
                    <th key={day} className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                      {day.slice(0, 3)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tableData.map((row, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {formatQhClock(row.time)}
                    </td>
                    {dayColumns.map(day => (
                      <td key={day} className="px-3 py-2 text-sm text-gray-900 text-right">
                        {formatNumber(row[day], formatType)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      } else if (compareLocations) {
        // Group by time and show locations/employees
        const groupedIntraday = safeIntradayData.reduce((acc, row) => {
          const entityKey = row?.location || row?.employee || row?.role;
          if (!row || !row.qh_num || !entityKey) return acc;
          const qh = row.qh_num;
          if (!acc[qh]) acc[qh] = { time: qh };
          if (safeSelectedMetrics[0]) {
            acc[qh][entityKey] = row[safeSelectedMetrics[0]];
          }
          return acc;
        }, {});

        const tableData = Object.values(groupedIntraday)
          .sort((a, b) => a.time - b.time);

        const locations = [...new Set(safeIntradayData.map(d => d.location || d.employee || d.role).filter(Boolean))];

        return (
          <div className={`overflow-auto ${isDashboard ? 'max-h-[600px]' : 'max-h-full'}`}>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  {locations.map(location => (
                    <th key={location} className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                      {location}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tableData.map((row, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {formatQhClock(row.time)}
                    </td>
                    {locations.map(location => (
                      <td key={location} className="px-3 py-2 text-sm text-gray-900 text-right">
                        {formatNumber(row[location], formatType)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      } else {
        // Show intraday data for selected metrics
        const tableData = safeIntradayData;

        return (
          <div className={`overflow-auto ${isDashboard ? 'max-h-[600px]' : 'max-h-full'}`}>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  {safeSelectedMetrics.map(metric => (
                    <th key={metric} className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                      {getMetricLabel(metric, safeMetrics)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tableData.map((row, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {formatQhClock(row.qh_num || 0)}
                    </td>
                    {safeSelectedMetrics.map(metric => (
                      <td key={metric} className="px-3 py-2 text-sm text-gray-900 text-right">
                        {formatNumber(row[metric], formatType)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
    }

    // Daily data
    if (!compareLocations) {
      const tableData = safeData;

      return (
        <div className={`overflow-auto ${isDashboard ? 'max-h-[600px]' : 'max-h-full'}`}>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                {safeSelectedMetrics.map(metric => (
                  <th key={metric} className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    {getMetricLabel(metric, safeMetrics)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tableData.map((row, index) => {
                const formattedDate = typeof row.date === "string" ? dateObjToShortStr(dateStrToObj(row.date)) : row.date;
                const displayDate = activeTab === "Weekly" ? `Week of ${formattedDate}` : formattedDate;
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {displayDate}
                    </td>
                    {safeSelectedMetrics.map(metric => (
                      <td key={metric} className="px-3 py-2 text-sm text-gray-900 text-right">
                        {formatNumber(row[metric], formatType)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    } else {
      // Compare locations/employees for daily
      const groupedData = safeRawData.reduce((acc, curr) => {
        if (!curr || !curr.date) return acc;
        const date = curr.date;
        if (!acc[date]) acc[date] = { date };
        const entityKey = curr.location || curr.employee || curr.role;
        if (entityKey && safeSelectedMetrics[0]) {
          acc[date][entityKey] = curr[safeSelectedMetrics[0]];
        }
        return acc;
      }, {});

      const tableData = Object.values(groupedData);
      const locations = [...new Set(safeRawData.map(d => d.location || d.employee || d.role).filter(Boolean))];

      return (
        <div className={`overflow-auto ${isDashboard ? 'max-h-[600px]' : 'max-h-full'}`}>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                {locations.map(location => (
                  <th key={location} className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    {location}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tableData.map((row, index) => {
                const formattedDate = typeof row.date === "string" ? dateObjToShortStr(dateStrToObj(row.date)) : row.date;
                const displayDate = activeTab === "Weekly" ? `Week of ${formattedDate}` : formattedDate;
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {displayDate}
                    </td>
                    {locations.map(location => (
                      <td key={location} className="px-3 py-2 text-sm text-gray-900 text-right">
                        {formatNumber(row[location], formatType)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
  };

  return (
    <div className="h-full bg-white">
      {renderTable()}
    </div>
  );
};

export default TableWidget;
